import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { BadgePill } from "./BadgePill";
import { InfoIcon } from "./icons";
import { resolveBadgeState } from "./handler/badge.helper";
import { formatMinutes } from "./handler/time.helper";
import { OtpOutcome } from "./handler/useOtpSend";
import { FlightFixture } from "./types";

export interface OptInStepProps {
  fixture: FlightFixture;
  onSubmit: (phoneNumber: string) => Promise<OtpOutcome>;
  isSubmitting: boolean;
  onBack: () => void;
}

const isPlausiblePhoneNumber = (raw: string): boolean =>
  raw.replace(/[^0-9]/g, "").length >= 7;

const isRateLimitErrorCode = (code?: string): boolean =>
  code === "OT_005" || code === "OT_006";

export const OptInStep: React.FC<OptInStepProps> = ({
  fixture,
  onSubmit,
  isSubmitting,
  onBack,
}) => {
  const { t } = useTranslation("flightAlerts");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [submitErrorCode, setSubmitErrorCode] = useState<string | undefined>(
    undefined,
  );

  const isArrival = fixture.direction === "arrival";
  const badge = resolveBadgeState(fixture);
  // The backend's own badgeDelayMinutes, passed straight through — never a local
  // `estimatedMinutes - scheduledMinutes` recompute (that's where the midnight-crossing bug
  // lived; see badge.helper.ts).
  const diffMinutes = fixture.badgeDelayMinutes ?? undefined;

  const resolvedOrEstimated =
    fixture.resolvedTimeMinutes !== undefined
      ? formatMinutes(fixture.resolvedTimeMinutes)
      : fixture.estimatedMinutes !== undefined
        ? formatMinutes(fixture.estimatedMinutes)
        : "—";

  const recapSubtitle = isArrival
    ? t("optin.recapArrival", {
        hall: fixture.hall ?? "—",
        date: fixture.dateDisplay,
        scheduled: formatMinutes(fixture.scheduledMinutes),
        resolved: resolvedOrEstimated,
      })
    : t("optin.recapDeparture", {
        airport: fixture.otherAirport,
        date: fixture.dateDisplay,
        scheduled: formatMinutes(fixture.scheduledMinutes),
        resolved: resolvedOrEstimated,
      });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isPlausiblePhoneNumber(phoneNumber) || isSubmitting) return;
    setSubmitErrorCode(undefined);
    const result = await onSubmit(phoneNumber);
    if (!result.success) {
      setSubmitErrorCode(result.errorCode ?? "UNKNOWN");
    }
  };

  return (
    <div className="flex flex-col gap-4 md:gap-5">
      <button
        type="button"
        onClick={onBack}
        className="self-start font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60"
      >
        {t("optin.backLink", { flightNumber: fixture.flightNumber })}
      </button>

      <div className="flex w-full flex-col gap-2 rounded-2xl border-2 border-ink-12 bg-card p-5 md:p-8">
        <div className="flex max-[355px]:flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-2">
            <span
              className={`inline-flex w-fit shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full py-[5px] pl-[10px] pr-[10px] ${
                isArrival ? "bg-ink-100" : "bg-primary-100"
              }`}
            >
              <span className="font-material-symbols text-[14px] leading-none text-white">
                {isArrival ? "flight_land" : "flight_takeoff"}
              </span>
              <span className="font-lexend text-[10px]/[12px] font-bold tracking-[0.6px] uppercase text-white">
                {isArrival
                  ? t("flight.badgeArriving")
                  : t("flight.badgeDeparting")}
              </span>
            </span>

            <h2 className="font-lexend text-[28px]/[32px] font-bold tracking-[-1px] text-ink-100">
              {fixture.flightNumber}
            </h2>
          </div>

          <BadgePill
            badge={badge}
            {...(diffMinutes !== undefined ? { diffMinutes } : {})}
          />
        </div>

        <p className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60">
          {recapSubtitle}
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 md:gap-5 rounded-2xl border-2 border-ink-12 bg-card p-5 md:p-8"
      >
        <h3 className="font-lexend text-[20px]/[24px] font-bold tracking-[-0.4px] text-ink-100">
          {t("optin.heading")}
        </h3>

        <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60 md:text-[16px]/[20px] md:tracking-[-0.1px]">
          {isArrival
            ? t("optin.descriptionArrival")
            : t("optin.descriptionDeparture")}
        </p>

        <div className="flex flex-col gap-2">
          <label
            htmlFor="optin-phone"
            className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60"
          >
            {t("optin.phoneLabel")}
          </label>
          <input
            id="optin-phone"
            aria-label={t("optin.phoneLabel")}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder={t("optin.phonePlaceholder")}
            className="h-14 rounded-2xl border-2 border-ink-12 bg-app px-4 font-lexend text-[16px]/[20px] font-normal tracking-[-0.1px] text-ink-100 placeholder:text-ink-40"
          />
        </div>

        {submitErrorCode && (
          <div className="flex items-start gap-2 rounded-xl bg-danger-tint p-3">
            <InfoIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-danger-100" />
            <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-danger-ink">
              {isRateLimitErrorCode(submitErrorCode)
                ? t("optin.errRateLimited")
                : t("optin.errSendFailed")}
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className={`flex h-14 w-full items-center justify-center rounded-2xl font-lexend text-[14px]/[18px] font-bold tracking-normal text-white md:text-[16px]/[20px] ${
            isSubmitting ? "cursor-not-allowed bg-gray-300" : "bg-primary-100"
          }`}
        >
          {isSubmitting ? t("optin.sending") : t("optin.submit")}
        </button>

        <p className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60">
          {t("optin.accountDisclaimer")}
        </p>
      </form>
    </div>
  );
};
